'use memo';

import { forwardRef, type KeyboardEvent, useEffect, useImperativeHandle, useRef, useState } from 'react';
import IconButton from '@/components/Button/IconButton';
import { Text } from '@/components/Typography';
import type { AiChatModel, ChatAgent } from '@/features/global-ai-chat/api/types';
import { isComposerSubmitKey } from '@/features/global-ai-chat/utils/composerKeyBinding';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { AiChatModelSelector } from './AiChatModelSelector';
export type AiChatComposerHandle = {
  focus: () => void;
};
type AiChatComposerProps = {
  models: AiChatModel[];
  selectedModelId: ChatAgent | null;
  placeholder: string;
  isSending: boolean;
  isDisabled?: boolean;
  disabledMessage?: string;
  onSelectModel: (modelId: ChatAgent) => void;
  onSend: (content: string) => Promise<void> | void;
};
export const AiChatComposer = forwardRef<AiChatComposerHandle, AiChatComposerProps>(function AiChatComposer({
  models,
  selectedModelId,
  placeholder,
  isSending,
  isDisabled = false,
  disabledMessage,
  onSelectModel,
  onSend
}, ref) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.globalAiChat'
  });
  const [content, setContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus()
  }));

  // 入力量に応じて高さを伸縮させる（3 行相当を下限、約 7 行で上限）。
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const BASE_HEIGHT = 72;
    const MAX_HEIGHT = 160;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, BASE_HEIGHT), MAX_HEIGHT)}px`;
  }, [content]);
  const canSend = content.trim().length > 0 && !isSending && !isDisabled;
  const handleSubmit = async () => {
    if (!canSend) return;
    const next = content.trim();
    setContent('');
    // 送信失敗時は呼び出し側（GlobalAiChatWidget）が発言を「失敗バブル + 再試行」として
    // 保持しエラー通知も行うため、ここでは入力復元せず送信するだけにする。
    await onSend(next);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd+Enter（Mac）/ Ctrl+Enter（Windows）で送信。Enter / Shift+Enter は改行。
    // IME 変換中の Enter（漢字確定など）は除外する。
    if (isComposerSubmitKey({
      key: event.key,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
      isComposing: event.nativeEvent.isComposing
    })) {
      event.preventDefault();
      void handleSubmit();
    }
  };
  if (isDisabled) {
    return <div className="flex items-center justify-center rounded-3xl border border-neutral-muted-border bg-neutral-muted-fill px-4 py-3">
        <Text variant="labelSmall" className="text-neutral-muted-on-fill">
          {disabledMessage ?? t('composerDisabledClosed')}
        </Text>
      </div>;
  }
  return <div className="flex flex-col gap-3 rounded-3xl border border-neutral-muted-border bg-neutral-fill px-3 py-3 shadow-sm">
      <textarea ref={textareaRef} value={content} onChange={event => setContent(event.target.value)} onKeyDown={handleKeyDown} rows={3} placeholder={placeholder} className={cn('w-full resize-none overflow-y-auto bg-transparent text-pSmall text-neutral-on-fill placeholder:text-neutral-muted-on-fill focus-visible:outline-none')} />
      <div className="flex items-center justify-between">
        <AiChatModelSelector models={models} selectedModelId={selectedModelId} onSelectModel={onSelectModel} />
        <IconButton icon="SendHorizontalIcon" variant="primary" size="Small" aria-label={t('sendMessageAriaLabel')} disabled={!canSend} isLoading={isSending} onClick={() => void handleSubmit()} />
      </div>
    </div>;
});